import { useEffect, useMemo, useState, useCallback } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ClipboardList, LayoutDashboard, FileText, Settings } from "lucide-react";
import { Header } from "@/components/Header";
import { AlertBanner } from "@/components/AlertBanner";
import { ArrivalForm } from "@/components/ArrivalForm";
import { RegisterTable } from "@/components/RegisterTable";
import { Filters } from "@/components/Filters";
import { Dashboard } from "@/components/Dashboard";
import { MonthlyReport } from "@/components/MonthlyReport";
import { PrintableReport } from "@/components/PrintableReport";
import { LoginDialog } from "@/components/LoginDialog";
import { AdminPanel } from "@/components/AdminPanel";
import { useRegistre } from "@/hooks/useRegistre";
import { useAuth } from "@/hooks/useAuth";
import { fetchSites, fetchLists } from "@/lib/api";
import { isOverstay, motifLabel } from "@/lib/format";

const PERIOD_LABELS = {
  today: "Aujourd'hui",
  yesterday: "Hier",
  week: "7 derniers jours",
  all: "Historique complet",
  custom: "Plage personnalisée",
};

function App() {
  const { isAdmin } = useAuth();
  const [filter, setFilter] = useState({ period: "today", from: "", to: "", site_id: localStorage.getItem("ssg_site") || "" });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [nowMs, setNowMs] = useState(Date.now());
  const [sites, setSites] = useState([]);
  const [lists, setLists] = useState({ motifs: [], services: [] });
  const [showLogin, setShowLogin] = useState(false);
  const agent = "Poste d'accueil";

  const { visitors, stats, connected, refresh } = useRegistre(filter);

  const loadSites = useCallback(() => fetchSites().then(setSites).catch(() => {}), []);
  const loadLists = useCallback(() => fetchLists().then(setLists).catch(() => {}), []);
  useEffect(() => { loadSites(); loadLists(); }, [loadSites, loadLists]);

  const setSiteId = (id) => {
    setFilter((f) => ({ ...f, site_id: id }));
    localStorage.setItem("ssg_site", id || "");
  };
  const siteName = sites.find((s) => s.id === filter.site_id)?.name || "";

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visitors.filter((v) => {
      if (q) {
        const hay = `${v.nom} ${v.societe} ${v.telephone} ${v.personne_visitee} ${v.service} ${v.badge} ${v.plaque} ${motifLabel(v)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (status === "present" && v.heure_depart) return false;
      if (status === "parti" && !v.heure_depart) return false;
      if (status === "overstay" && !isOverstay(v, nowMs)) return false;
      return true;
    });
  }, [visitors, search, status, nowMs]);

  const overstayCount = useMemo(
    () => visitors.filter((v) => isOverstay(v, nowMs)).length,
    [visitors, nowMs]
  );

  const filterLabel = PERIOD_LABELS[filter.period] || "";

  return (
    <div className="min-h-screen bg-[#F1F5F9] overflow-x-hidden">
      <Header connected={connected} onLoginClick={() => setShowLogin(true)} />
      <Toaster position="top-right" richColors />
      <LoginDialog open={showLogin} onOpenChange={setShowLogin} />
      <PrintableReport visitors={filtered} stats={stats} filterLabel={filterLabel} />

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <AlertBanner count={overstayCount} />

        <Tabs defaultValue="registre" className="w-full">
          <TabsList className="bg-white border border-slate-200 shadow-sm p-1 h-auto flex flex-wrap w-full sm:w-auto no-print">
            <TabsTrigger
              value="registre"
              className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white px-3 sm:px-5 py-2.5 gap-2 font-semibold text-sm"
              data-testid="tab-registre"
            >
              <ClipboardList className="h-4 w-4" /> Registre en direct
            </TabsTrigger>
            <TabsTrigger
              value="dashboard"
              className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white px-3 sm:px-5 py-2.5 gap-2 font-semibold text-sm"
              data-testid="tab-dashboard"
            >
              <LayoutDashboard className="h-4 w-4" /> Tableau de bord
            </TabsTrigger>
            <TabsTrigger
              value="rapport"
              className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white px-3 sm:px-5 py-2.5 gap-2 font-semibold text-sm"
              data-testid="tab-rapport"
            >
              <FileText className="h-4 w-4" /> Rapport mensuel
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger
                value="admin"
                className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white px-3 sm:px-5 py-2.5 gap-2 font-semibold text-sm"
                data-testid="tab-admin"
              >
                <Settings className="h-4 w-4" /> Administration
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="registre" className="mt-6">
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
              <div className="xl:col-span-5 2xl:col-span-4">
                <ArrivalForm
                  agent={agent}
                  motifs={lists.motifs}
                  services={lists.services}
                  siteId={filter.site_id}
                  siteName={siteName}
                />
              </div>
              <div className="xl:col-span-7 2xl:col-span-8 space-y-4">
                <Filters
                  filter={filter}
                  setFilter={setFilter}
                  search={search}
                  setSearch={setSearch}
                  status={status}
                  setStatus={setStatus}
                  sites={sites}
                  siteId={filter.site_id}
                  setSiteId={setSiteId}
                  onPrint={() => window.print()}
                />
                <div className="flex items-center justify-between px-1 no-print">
                  <p className="text-sm text-slate-500">
                    <span className="font-semibold text-slate-700">{filtered.length}</span> entrée
                    {filtered.length > 1 ? "s" : ""} · {filterLabel}
                  </p>
                </div>
                <RegisterTable visitors={filtered} nowMs={nowMs} onChanged={refresh} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="dashboard" className="mt-6">
            <Dashboard stats={stats} visitors={filtered} filterLabel={filterLabel} />
          </TabsContent>

          <TabsContent value="rapport" className="mt-6">
            <MonthlyReport sites={sites} defaultSiteId={filter.site_id} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="admin" className="mt-6">
              <AdminPanel onSitesChanged={loadSites} onListsChanged={loadLists} />
            </TabsContent>
          )}
        </Tabs>
      </main>

      <footer className="text-center text-xs text-slate-400 py-6 no-print">
        Registre des visiteurs · SSG Sécurité Guinée · Synchronisation temps réel entre postes
      </footer>
    </div>
  );
}

export default App;
